import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { cn } from '@helpers/cn';

const CardClickContext = createContext<(() => void) | undefined>(undefined);

export const CardInteractive = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function CardInteractive({ children, className, onClick, ...props }, ref) {
  const onCardClick = useContext(CardClickContext);

  return (
    <div
      {...props}
      ref={ref}
      className={cn('eye:relative eye:z-20', className)}
      onClick={(event) => {
        onClick?.(event);
        onCardClick?.();
      }}
    >
      {children}
    </div>
  );
});

interface TableItemCardProps {
  main: ReactNode;
  onClick?: () => void;
  title: ReactNode;
  titleAction?: ReactNode;
}

const TableItemCard = ({
  main,
  onClick,
  title,
  titleAction,
}: TableItemCardProps) => {
  const titleId = useId();

  return (
    <CardClickContext.Provider value={onClick}>
      <div className='eye:group/table-item-card eye:relative eye:w-full'>
        {onClick ? (
          <button
            type='button'
            aria-labelledby={titleId}
            onClick={onClick}
            className='eye:absolute eye:inset-0 eye:z-10 eye:cursor-pointer eye:rounded-md eye:outline-black eye:outline-offset-3 eye:focus-visible:outline-2 eye:hover:outline-2'
          />
        ) : null}
        <div className='eye:overflow-hidden eye:rounded-md eye:border eye:border-grey-20 eye:bg-white'>
          <div
            id={titleId}
            className={cn(
              'eye:px-2xs eye:py-4xs eye:body-m-highlight eye:text-left eye:bg-blue-25 eye:group-has-data-[title-highlighted=true]/table-item-card:bg-yellow-35',
              { 'eye:pr-l': !!titleAction },
            )}
          >
            {title}
          </div>

          <div className='eye:w-full eye:p-2xs'>{main}</div>
        </div>

        {titleAction ? (
          <div className='eye:pointer-events-none eye:absolute eye:inset-x-0 eye:top-0 eye:z-20 eye:flex eye:items-center eye:justify-end eye:px-2xs eye:py-4xs'>
            <div className='eye:pointer-events-auto'>{titleAction}</div>
          </div>
        ) : null}
      </div>
    </CardClickContext.Provider>
  );
};

export default TableItemCard;
